import { useState, useMemo, ChangeEvent, useRef } from 'react';
import classNames from 'classnames';
import { InputTextarea as PInputTextArea } from 'primereact/inputtextarea';
import { IInputTextarea } from './InputTextArea.types';
import styles from './InputTextArea.module.scss';

export const InputTextArea = (props: IInputTextarea) => {
  const {
    ref,
    label,
    required,
    className,
    id,
    disabled,
    debounceTime,
    placeholder = '',
    hasChanged = false,
    highlightOnChange = true,
    inputChangedStyle,
    ...restProps
  } = props;
  const internalRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = ref || internalRef;
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [touched, setTouched] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const inputId = useMemo(() => id || `text-area-${Math.random().toString(36).substring(2, 9)}`, [id]);
  const isHavingError = !isValid && touched;
  const inputClassName = classNames(styles['ds-input-text-area'], className, {
    [styles['text-area-error']]: isHavingError,
  });

  const checkValidity = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const input = event.target;
    const isFieldValid = input.checkValidity();
    setIsValid(isFieldValid);
    if (!isFieldValid) {
      if (input.validity.valueMissing) {
        setErrorMessage('Field is required');
      }
    }
  };

  const onInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setTouched(true);
    checkValidity(event);

    if (restProps?.onChange) {
      /**
       * - If a debounce time is specified, delays the execution of the `onChange` callback by the given debounce time (in milliseconds) to avoid triggering the function immediately on every keystroke.
       * - If no debounce time is set, the `onChange` callback is executed immediately.
       */
      if (debounceTime && debounceTime > 0) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          if (restProps?.onChange) restProps.onChange(event);
        }, debounceTime);
      } else {
        restProps.onChange(event);
      }
    }
  };

  const getClassNames = () => {
    return `${inputClassName} ${!!restProps.value ? styles['input-filled'] : ''} ${
      highlightOnChange && !isHavingError && hasChanged ? `input-changed ${inputChangedStyle}` : ''
    }`;
  };

  const InputTextAreaElement = (
    <PInputTextArea
      {...restProps}
      ref={inputRef}
      required={required}
      disabled={disabled}
      id={inputId}
      value={restProps.value || ''}
      className={getClassNames()}
      placeholder={placeholder}
      onChange={onInputChange}
      onBlur={(event) => {
        setTouched(true);
        if (restProps?.onBlur) restProps.onBlur(event);
      }}
    />
  );

  if (!label) {
    return InputTextAreaElement;
  }

  return (
    <div className={classNames(styles['ds-input-text-area-wrapper'], { [styles['disabled']]: disabled })}>
      {label && (
        <label htmlFor={inputId}>
          {label} {required && <span className={styles['required-asterisk']}>*</span>}
        </label>
      )}
      {InputTextAreaElement}
      {isHavingError && errorMessage && <span className={styles['error-message']}>{errorMessage}</span>}
    </div>
  );
};
